import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import PageLayout from "components/layout/PageLayout";
import { useSupportChats } from "../../customer/support/hooks";
import CustomerPaginations from "components/pagination/CustomerPaginations";
import AppSpinner from "components/spinner/AppSpinner";
import { managersSelector } from "store/project/selector";
import { useSupportUnreadContext } from "contexts/SupportUnreadContext";

const CHATS_PAGE_LIMIT = 10;

const toDisplayDate = (dateValue: unknown): string => {
  if (!(typeof dateValue === "string" || typeof dateValue === "number")) return "";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  if (d >= startOfToday) return "Today";
  if (d >= startOfYesterday) return "Yesterday";
  return d.toLocaleDateString();
};

const isChatClosed = (rawStatus: unknown): boolean => {
  return (
    rawStatus === "closed" ||
    rawStatus === "CLOSED" ||
    rawStatus === 0 ||
    rawStatus === false
  );
};

export default function SupportTopics() {
  const navigate = useNavigate();
  const location = useLocation();
  const { chats, meta, fetchChats, isLoading } = useSupportChats();
  const managers = useSelector(managersSelector) as any[];
  const { refetch: refetchUnreadCount } = useSupportUnreadContext();
  const [page, setPage] = useState(1);

  const managersMap = useMemo(() => {
    const map: Record<string, string> = {};
    (managers ?? []).forEach((m: any) => {
      const name = `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim() || m.email || `ID ${m.user_id}`;
      map[String(m.user_id)] = name;
    });
    return map;
  }, [managers]);

  useEffect(() => {
    fetchChats({ page, per_page: CHATS_PAGE_LIMIT });
    // Refresh unread count when support topics page loads
    refetchUnreadCount();
  }, [fetchChats, page, refetchUnreadCount]);

  const chatItems = (chats ?? [])
    .map((chat: any) => {
      const id = chat?.chat_id;
      const title = chat?.topic ?? chat?.title ?? chat?.subject ?? "Support request";
      const orderId =
        chat?.order_id ??
        chat?.orderId ??
        chat?.order?.order_id ??
        chat?.order?.id ??
        null;
      const rawStatus = chat?.status ?? chat?.state ?? (chat?.is_closed ? "closed" : "open");
      const displayDate = chat?.updated_at ?? chat?.last_message_at ?? chat?.created_at;
      const assignedManagerId = chat?.assigned_manager_id ?? null;
      const unreadCount = chat?.unread_count ?? chat?.unreadCount ?? 0;

      return {
        id,
        title,
        orderId,
        isClosed: isChatClosed(rawStatus),
        displayDate: toDisplayDate(displayDate),
        assignedManagerId,
        assignedManagerName: assignedManagerId != null
          ? managersMap[String(assignedManagerId)] ?? `ID ${assignedManagerId}`
          : null,
        unreadCount,
      };
    })
    .filter((it: any) => it?.id != null);

  const perPage = CHATS_PAGE_LIMIT;
  const totalCount = meta?.total ?? 0;
  const totalPages = meta?.lastPage ?? Math.max(1, Math.ceil(totalCount / perPage));
  const showPagination = totalCount > perPage;

  const onSelect = useCallback(
    (chat: any) => {
      // Determine current layout from pathname
      const currentLayout = location.pathname.startsWith('/manager') ? '/manager' : '/admin';
      navigate(`${currentLayout}/support/${chat.id}`, {
        state: { chat },
      });
    },
    [navigate, location.pathname]
  );

  return (
    <PageLayout topContent={<Box h={{ base: "60px", md: "80px" }} />}>
    <Box
        w="100%"
        bg="#F5F7FA"
        p="24px"
        border="1px solid #E6ECF3"
        borderRadius="12px"
      >
      <Box
        w="100%"
        bg="#FFF"
        p="24px"
        border="1px solid #E6ECF3"
        borderRadius="12px"
      >
        <Text fontSize="16px" fontWeight="600" lineHeight="1.3129" color="#0B0E10" mb="16px">
          Support topics
        </Text>

        {isLoading ? (
          <Flex align="center" justify="center" minH="200px">
            <AppSpinner />
          </Flex>
        ) : chatItems.length === 0 ? (
          <Text fontSize="14px" color="#6B7280">
            No topics.
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Chat ID</Th>
                  <Th>Topic</Th>
                  <Th>Order ID</Th>
                  <Th>Assigned Manager</Th>
                  <Th>Status</Th>
                  <Th>Last update</Th>
                </Tr>
              </Thead>
              <Tbody>
                {chatItems.map((chat: any) => (
                  <Tr
                    key={String(chat.id)}
                    cursor="pointer"
                    _hover={{ bg: "#F5F9FF" }}
                    onClick={() => onSelect(chat)}
                  >
                    <Td>
                      <Flex alignItems="center" gap="8px">
                        <Box>
                          {chat.unreadCount > 0 && (
                            <Badge
                              bgColor="#EF4444"
                              color="#FFF"
                              fontSize="10px"
                              fontWeight="600"
                              borderRadius="999px"
                              px="4px"
                              py="1px"
                              pl="2px"
                              pr="2px"
                              minW="16px"
                              h="16px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              {chat.unreadCount}
                            </Badge>
                          )}
                        </Box>
                        <Text fontSize="14px" color="#505E70">
                          #{String(chat.id)}
                        </Text>
                      </Flex>
                    </Td>
                    <Td>
                      <Text fontSize="14px" fontWeight="600" color="#0B0E10">
                        {chat.title}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="14px" color="#505E70">
                        {chat.orderId != null && Number(chat.orderId) !== 0 ? `#${String(chat.orderId)}` : "—"}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="14px" color="#505E70">
                        {chat.assignedManagerName ?? "—"}
                      </Text>
                    </Td>
                    <Td>
                      <Badge
                        h="28px"
                        px="10px"
                        borderRadius="8px"
                        fontSize="13px"
                        fontWeight="400"
                        bg={chat.isClosed ? "#9CA3AF" : "#BFD7FF"}
                        color={chat.isClosed ? "#FFF" : "#0B0E10"}
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        textTransform="none"
                      >
                        {chat.isClosed ? "Closed" : "Open"}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="14px" color="#505E70">
                        {chat.displayDate}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {showPagination && (
          <Box pt="16px">
            <CustomerPaginations
              totalCount={totalCount}
              totalPages={totalPages}
              currentPage={page}
              perPage={perPage}
              onChangePage={setPage}
              showPageSizeSelector={false}
            />
          </Box>
        )}
        </Box>
      </Box>
    </PageLayout>
  );
}
